import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import Pusher from 'pusher-js';
import { getRealtimeConfig } from 'services/notifications/realtimeNotifications';

const PusherContext = createContext<Pusher | null>(null);

type PusherProviderProps = {
  children: ReactNode;
};

export function PusherProvider({ children }: PusherProviderProps) {
  const [client, setClient] = useState<Pusher | null>(null);
  const clientRef = useRef<Pusher | null>(null);
  const destroyingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    destroyingRef.current = false;
    const accessToken = Cookies.get('accesstoken');

    if (!accessToken) {
      return undefined;
    }

    getRealtimeConfig()
      .then((config: Record<string, unknown>) => {
        if (cancelled) return;

        const pusher = new Pusher(config.key as string, {
          cluster: config.cluster as string,
          wsHost: config.wsHost as string,
          httpHost: config.httpHost as string,
          forceTLS: config.forceTLS as boolean,
          disableStats: config.disableStats as boolean,
          enabledTransports: config.enabledTransports as (
            | 'ws'
            | 'wss'
            | 'xhr_streaming'
            | 'xhr_polling'
            | 'sockjs'
          )[],
          authEndpoint: '/api/realtime/auth/',
          auth: {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        });

        const onDisconnected = () => {
          if (destroyingRef.current) return;
          console.warn('[Pusher] disconnected');
        };

        pusher.connection.bind(
          'state_change',
          ({ previous, current }: { previous: string; current: string }) => {
            if (destroyingRef.current) return;
            console.log(`[Pusher] connection: ${previous} → ${current}`);
          }
        );

        pusher.connection.bind('connected', () => {
          if (destroyingRef.current) return;
          console.log(
            '[Pusher] connected. socket_id:',
            pusher.connection.socket_id
          );
        });

        pusher.connection.bind('error', (err: { error?: { data?: unknown } }) => {
          if (destroyingRef.current) return;
          console.error('[Pusher] connection error:', err);
        });

        pusher.connection.bind('failed', () => {
          if (destroyingRef.current) return;
          console.error('[Pusher] connection failed — no supported transport');
        });

        pusher.connection.bind('disconnected', onDisconnected);

        clientRef.current = pusher;
        setClient(pusher);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[Pusher] config unavailable — realtime disabled', err);
        }
      });

    return () => {
      cancelled = true;
      destroyingRef.current = true;
      const pusher = clientRef.current;
      if (pusher) {
        pusher.connection.unbind('state_change');
        pusher.connection.unbind('connected');
        pusher.connection.unbind('error');
        pusher.connection.unbind('failed');
        pusher.connection.unbind('disconnected');
        pusher.disconnect();
      }
      clientRef.current = null;
      setClient(null);
    };
  }, []);

  return (
    <PusherContext.Provider value={client}>{children}</PusherContext.Provider>
  );
}

export function usePusher(): Pusher | null {
  return useContext(PusherContext);
}
